import { Link, NavLink, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { navMenu } from '../content/siteContent';
import ClayIcon from './v2/ClayIcon';
import { brandAssets } from '../content/brandAssets';

function Chevron({ open }) {
  return (
    <svg className={`nav-chev${open ? ' is-open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** Desktop dropdown: page sections on the left, spoke pages (cities / features) on the right. */
function MenuPanel({ item, id, onNavigate }) {
  return (
    <div id={id} className="nav-panel" role="menu" aria-label={item.label}>
      <div className="nav-panel-main">
        <Link to={item.to} className="nav-panel-head" onClick={onNavigate} role="menuitem">
          <span className="fw-display">{item.label}</span>
          <span className="nav-panel-blurb">{item.blurb}</span>
        </Link>
        <ul className="nav-panel-grid">
          {item.sections.map((s) => (
            <li key={s.to}>
              <Link to={s.to} onClick={onNavigate} role="menuitem" className="nav-panel-link">
                <ClayIcon name={s.icon} size={30} />
                {s.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      {item.more && (
        <div className="nav-panel-side">
          <div className="fw-mono nav-panel-title">{item.more.title}</div>
          <ul>
            {item.more.links.map((l) => (
              <li key={l.to}>
                <Link to={l.to} onClick={onNavigate} role="menuitem">
                  {l.label}
                  {l.note && <span className="nav-panel-note">{l.note}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // desktop dropdown label
  const [mobileSection, setMobileSection] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);
  const hoverTimer = useRef(0);

  // Close everything on navigation.
  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
    setMobileSection(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
        setOpenMenu(null);
      }
    };
    const onClick = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) setOpenMenu(null);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onClick);
    };
  }, []);

  // While the mobile menu is open, tuck away the bottom dock and back-to-top so they don't cover it.
  useEffect(() => {
    document.documentElement.classList.toggle('menu-open', mobileOpen);
    return () => document.documentElement.classList.remove('menu-open');
  }, [mobileOpen]);

  const closeAll = () => {
    setOpenMenu(null);
    setMobileOpen(false);
  };

  // Small delay on hover-out so the pointer can travel from the trigger into the panel.
  const hoverOpen = (label) => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    clearTimeout(hoverTimer.current);
    setOpenMenu(label);
  };
  const hoverClose = () => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpenMenu(null), 160);
  };

  const isActive = (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

  return (
    <header className="nav-shell" ref={navRef}>
      <div className="glass nav-pill">
        <Link to="/" style={{ display: 'flex', alignItems: 'center', minHeight: 44, flex: 'none' }} aria-label="FastWhistle home">
          <img className="nav-logo" src={brandAssets.logo} alt="FastWhistle" width="360" height="86" fetchPriority="high" />
        </Link>

        <nav className="navlinks" aria-label="Primary">
          {navMenu.map((item) =>
            item.sections ? (
              <div
                key={item.label}
                className="nav-item"
                onMouseEnter={() => hoverOpen(item.label)}
                onMouseLeave={hoverClose}
              >
                <button
                  type="button"
                  className={`nav-trigger${isActive(item) ? ' is-active' : ''}`}
                  aria-expanded={openMenu === item.label}
                  aria-controls={`menu-${item.label}`}
                  onClick={() => setOpenMenu((cur) => (cur === item.label ? null : item.label))}
                >
                  {item.label}
                  <Chevron open={openMenu === item.label} />
                </button>
                {openMenu === item.label && <MenuPanel item={item} id={`menu-${item.label}`} onNavigate={closeAll} />}
              </div>
            ) : (
              <NavLink key={item.label} to={item.to} className={({ isActive: a }) => (a && !item.to.includes('#') ? 'is-active' : undefined)}>
                {item.label}
              </NavLink>
            ),
          )}
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Link to="/contact-us" className="clay-soft btn btn-mag nav-cta" style={{ display: 'inline-flex', alignItems: 'center', minHeight: 46, padding: '0 22px', borderRadius: 999, background: '#3462F2', color: '#fff', textDecoration: 'none', fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap' }}>
            Book a demo
          </Link>
          <button
            type="button"
            className="glass menu-btn btn"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
            onClick={() => setMobileOpen((open) => !open)}
            style={{ width: 46, height: 46, borderRadius: '50%', alignItems: 'center', justifyContent: 'center', color: 'var(--ink)', padding: 0 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              {mobileOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      <nav id="mobile-menu" className={`glass mobile-menu${mobileOpen ? ' is-open' : ''}`} aria-label="Mobile">
        {navMenu.map((item) =>
          item.sections ? (
            <div key={item.label} className="m-group">
              <button
                type="button"
                className="m-group-btn"
                aria-expanded={mobileSection === item.label}
                onClick={() => setMobileSection((cur) => (cur === item.label ? null : item.label))}
              >
                {item.label}
                <Chevron open={mobileSection === item.label} />
              </button>
              {mobileSection === item.label && (
                <div className="m-group-body">
                  <Link to={item.to} className="m-group-hub" onClick={closeAll}>
                    {item.label} overview →
                  </Link>
                  <div className="m-group-grid">
                    {item.sections.filter((s) => !s.to.endsWith('#overview')).map((s) => (
                      <Link key={s.to} to={s.to} onClick={closeAll}>
                        <ClayIcon name={s.icon} size={24} />
                        {s.label}
                      </Link>
                    ))}
                  </div>
                  {item.more && (
                    <>
                      <div className="fw-mono nav-panel-title" style={{ padding: '10px 14px 4px' }}>{item.more.title}</div>
                      <div className="m-group-chips">
                        {item.more.links.map((l) => (
                          <Link key={l.to} to={l.to} className="chip" onClick={closeAll}>
                            {l.label}
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <Link key={item.label} to={item.to} onClick={closeAll}>
              {item.label}
            </Link>
          ),
        )}
        <Link to="/contact-us" onClick={closeAll} className="m-menu-cta">Book a demo</Link>
      </nav>
    </header>
  );
}
